import { useFormEvents } from "../composables/use-form-events.js";
import { type InputState } from "../composables/use-input-state.js";
import type { FormTrackingName } from "../form-settings/form-experience/form-experience-settings.js";
import type { GatingState } from "../types/definitions/gating/gating-state.js";
import { type MaybeRefOrGetter, watch } from "vue";
import { useFormLogger } from "../composables/use-form-logger.js";
import type { ILogger } from "../../utils/Logger/ILogger.js";
import { toValue } from "vue";
import type { FormWidgetData } from "../../widgetDataTs.js";
import type { HiddenMarketoInputType } from "../types/definitions/inputs/marketo-input-names-and-values.js";
import { type PartialWithNil } from "@mcwd/typescript-type-guards";
import { removeEmptyProperties } from "@mcwd/util-parse-and-convert";
import type { MarketoKnownUserStatus } from "../../tracking/data/functions/getInitialMarketoKnownUserStatus.js";
import { ValueGetters } from "../../tracking/data/ValueGetters.js";

//model used to send data to TagManager
interface AbandonmentData {
  event: string;
  formName: FormTrackingName;
  widgetName: string;
  widgetVariation: string;
  gatingState: GatingState.MarketoForm;
  isInModal: boolean;
  offerId: string | null;
  tacticId: string;
  InitialMarketoKnownUserStatus: MarketoKnownUserStatus;
  fieldInteractionOrder?: string | null;
  fieldsWithError?: string | null;
  touchedFieldsNoValue?: string | null;
  emptyFields?: string | null;
  validationErrors?: string | null;
}
type OptionalAbandonmentData = {
  [K in keyof AbandonmentData]?: AbandonmentData[K] | null | '';
}
/**
 * Classs to help track form abandonment and send data to GA4
 */
export class FormAbandonmentManager {

  InputState: Record<string, InputState>;
  FieldInteractionOrder: Set<string>; // sets dont allow duplicates
  TouchedFieldsNoValue: Array<string>;
  EmptyFields: Array<string>;
  ValidationErrors: Array<string>;
  FieldsWithError: Array<string>;
  FormName: FormTrackingName;
  GatingState: GatingState.MarketoForm;
  IsInModal: MaybeRefOrGetter<boolean>;
  widgetData: FormWidgetData;
  tactic: string;
  #logger: ILogger;

  //initiate class data and field interaction order tracking
  constructor({
    formName,
    gatingState,
    isInModal,
    inputStates,
    widgetData,
    hiddenFieldValue
  }: {
    formName: FormTrackingName;
    gatingState: GatingState.MarketoForm;
    isInModal: MaybeRefOrGetter<boolean>;
    inputStates: Record<string, InputState>;
    widgetData: FormWidgetData;
    hiddenFieldValue: MaybeRefOrGetter<PartialWithNil<HiddenMarketoInputType>>;
  }) {
    const { logger } = useFormLogger();
    this.#logger = logger;
    this.InputState = inputStates;
    this.FormName = formName;
    this.GatingState = gatingState;
    this.IsInModal = isInModal;
    this.tactic = toValue(hiddenFieldValue).Campaign__c ?? "";
    this.widgetData = widgetData;
    this.FieldInteractionOrder = new Set<string>();
    this.TouchedFieldsNoValue = [];
    this.ValidationErrors = [];
    this.FieldsWithError = [];
    this.EmptyFields = [];
    this.#trackFieldsFill();
  }
  /**
   * Tracks field interaction based on user click
   */
  #trackFieldsFill() {
    const events = useFormEvents();
    //wait till the form is set up
    events.ready.on(() => {
      //deep watch the InputState
      const removeWatcher = watch(() => this.InputState, (newValue) => {
        for (const key in newValue) {
          //if a field is active then add to order
          if (newValue[key].active) {
            //track active field
            this.FieldInteractionOrder.add(key);
          }
        }
        //once all the fields have been active remove watcher
        if (this.FieldInteractionOrder.size === Object.keys(this.InputState).length){
          removeWatcher();
        }
      }, { deep: true });
    });
  }
  /**
   * Gets the widget name that initiated the form
   */
  get widgetName() {
    return toValue(this.widgetData).widget.name ?? "";
  }
  /**
   * Gets the widget variation which initiated the form
   */
  get widgetVariation() {
    return toValue(this.widgetData).widget.variation ?? "";
  }
  /**
   * 
   */
  get OfferId() {
    return toValue(this.widgetData).setup.tracking.offerId ?? "";
  }
  captureLastInputState(){
    for (const key in this.InputState) {      
      const FormField = this.InputState[key];
      if(FormField.isEmpty){
      //get the field with has been touched but left empty
        if(FormField.touched && this.FieldInteractionOrder.has(key))
          this.TouchedFieldsNoValue.push(key);
        else // non touched empty fields
          this.EmptyFields.push(key);
      }
      //get all validation error at the time of abandonment
      const { isValid, ValidationType } = FormField.validationCheck;
      if (!isValid && ValidationType) {
        this.ValidationErrors.push(ValidationType);
      }
      //get all the fields that has an error at abandonment
      if(FormField.showError){
        this.FieldsWithError.push(key);
      }
    }
  }
  /**
   * Parse inputState obj into a model that can be send to GA4
   */
  #createObj(): AbandonmentData {
    // form state data
    const FormStateData: OptionalAbandonmentData = {
      fieldInteractionOrder: Array.from(this.FieldInteractionOrder).join(","),
      fieldsWithError: this.FieldsWithError.join(","),
      touchedFieldsNoValue: this.TouchedFieldsNoValue.join(","),
      validationErrors: this.ValidationErrors.join(","),
      emptyFields: this.EmptyFields.join(","),
    };

    const nonEmptyValues = removeEmptyProperties(FormStateData);
    return {
      ...nonEmptyValues,
      event: "FormAbandonment",
      formName: this.FormName,
      widgetName: this.widgetName,
      widgetVariation: this.widgetVariation,
      gatingState: this.GatingState,
      offerId: this.OfferId,
      tacticId: this.tactic,
      isInModal: toValue(this.IsInModal),
      InitialMarketoKnownUserStatus: ValueGetters.InitialMarketoKnownUserStatus.value
    };
  }
  /**
   * Sends data to the dataLayer for GA4
   */
  sendAbandonmentData() {
    try {
      const FormAbandonmentObj = this.#createObj();
      this.#logger.debug("About to send Form Abandonment event", FormAbandonmentObj);
      window.McTagManager.sendEvent(FormAbandonmentObj);
    }
    catch (err) {
      this.#logger.error(`Failed to send Form Abandonment data: `, err);
    }
  }
}